import clsx from "classnames";
import { Image } from "@/ui";
import ArrowRight from "@/assets/ArrowRight.svg";
import classes from "../styles.module.scss";

/**
 * One highlight card. Rendered as a link when it has a URL, so the whole card is clickable;
 * the button inside is a styled span to avoid nesting links.
 *
 * @param {Object} [card] - Card item: image, tag, title, text, ctaLabel, href, open_in_new_tab, tint, buttonVariant.
 * @param {number} [index] - Zero-based card index, used for the editor prop paths.
 * @param {boolean} [wide] - Single-card layout: text sits on the left over a side gradient.
 * @returns {JSX.Element}
 */
export default function HighlightCard({ card = {}, index = 0, wide = false }) {
  const { image, tag, title, text, ctaLabel, href, open_in_new_tab, tint = "secondary", buttonVariant = "tertiary" } = card;
  const Tag = href ? "a" : "div";
  const linkProps = href
    ? { href, target: open_in_new_tab ? "_blank" : undefined, rel: open_in_new_tab ? "noopener noreferrer" : undefined }
    : {};

  return (
    <Tag
      {...linkProps}
      className={clsx(classes.highlightCard, classes[`highlightCard--${tint}`], { [classes["highlightCard--wide"]]: wide })}
    >
      {image?.url && (
        <div className={classes.highlightCard__media} data-sourceflow-prop={`cards.${index}.image`}>
          <Image img={image.url} size="1200x" alt={image.alt ?? ""} />
        </div>
      )}
      <div className={classes.highlightCard__overlay} aria-hidden="true" />

      <div className={classes.highlightCard__body}>
        {!!tag && (
          <span className={classes.highlightCard__tag} data-sourceflow-prop={`cards.${index}.tag`}>
            {tag}
          </span>
        )}

        {!!title && (
          <h3 className={classes.highlightCard__title} data-sourceflow-prop={`cards.${index}.title`}>
            {title}
          </h3>
        )}

        {!!text && (
          <div
            className={classes.highlightCard__text}
            data-sourceflow-prop={`cards.${index}.text`}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        )}

        {!!ctaLabel && (
          <span className={clsx("btn", `btn-${buttonVariant}`, classes.highlightCard__cta)} data-sourceflow-prop={`cards.${index}.ctaLabel`}>
            {ctaLabel}
            <ArrowRight />
          </span>
        )}
      </div>
    </Tag>
  );
}
